import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/v5/components/ui/dialog";
import { Button, buttonVariants } from "@/v5/components/ui/button";
import { TiptapEditor } from "@/v5/components/ui/tiptap-editor";
import { EmailTemplatePicker } from "@/v5/components/EmailTemplatePicker";
import { EmojiPickerButton } from "@/v5/components/EmojiPickerButton";
import { DocumentSelectModal } from "@/v5/components/DocumentSelectModal";
import { RecipientPicker } from "@/v5/components/RecipientPicker";
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  Paperclip,
  Reply,
  Send,
  Smile,
  X,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Editor } from "@/v5/components/ui/tiptap-editor";
import { toast } from "@/store/toastStore";
import { getErrorMessage } from "@/services/apiClient";

interface EmailReplyTo {
  subject: string;
  htmlBody: string;
  replyID: string;
}

interface EmailComposeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientName: string;
  patientEmail?: string;
  patientId?: string;
  planId?: string;
  onSend: (
    subject: string,
    htmlBody: string,
    replyID?: string,
    recipients?: string[],
    files?: File[],
    existingDocuments?: string[],
  ) => Promise<void>;
  replyTo?: EmailReplyTo;
}

// Legacy plupload filter parity (assets-ftp/js/patient-details-test.js).
const ALLOWED_EXTENSIONS = [
  "jpg", "jpeg", "png", "gif", "pdf", "doc", "rtf", "docx", "xls", "xlsx", "mp3", "mp4",
];
const MAX_FILE_BYTES = 15 * 1024 * 1024; // Legacy plupload max_file_size: 15mb.
const ACCEPT_ATTR = ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(",");

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function EmailComposeModal({
  open,
  onOpenChange,
  patientName,
  patientEmail,
  patientId,
  planId,
  onSend,
  replyTo,
}: EmailComposeModalProps) {
  const [subject, setSubject] = useState("");
  const [htmlBody, setHtmlBody] = useState("");
  const [sending, setSending] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [docPickerOpen, setDocPickerOpen] = useState(false);
  const editorRef = useRef<Editor | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const subjectRef = useRef<HTMLInputElement | null>(null);
  // Which field the emoji button writes into: the one the user was last in.
  // null until either is focused, which puts the emoji at the end of the body.
  const lastFieldRef = useRef<"subject" | "body" | null>(null);

  const [showOriginal, setShowOriginal] = useState(false);

  useEffect(() => {
    if (open && replyTo) {
      const reSubject = replyTo.subject.startsWith("Re:")
        ? replyTo.subject
        : `Re: ${replyTo.subject}`;
      setSubject(reSubject);
      setHtmlBody("");
      setShowOriginal(false);
      setRecipients([]);
      setFiles([]);
      setSelectedDocs([]);
      lastFieldRef.current = null;
    }
  }, [open, replyTo]);

  const reset = useCallback(() => {
    setSubject("");
    setHtmlBody("");
    setSending(false);
    setShowOriginal(false);
    setRecipients([]);
    setFiles([]);
    setSelectedDocs([]);
    lastFieldRef.current = null;
  }, []);

  const insertEmoji = useCallback((emoji: string) => {
    const input = subjectRef.current;
    if (lastFieldRef.current === "subject" && input) {
      // An input keeps its selection after it blurs, so this is where the
      // caret was when the emoji button was clicked. A selection is replaced.
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? start;
      setSubject(input.value.slice(0, start) + emoji + input.value.slice(end));
      requestAnimationFrame(() => {
        input.focus();
        const caret = start + emoji.length;
        input.setSelectionRange(caret, caret);
      });
      return;
    }

    const ed = editorRef.current;
    if (ed) {
      // ProseMirror keeps its selection while blurred; focus() restores it.
      ed.chain().focus(lastFieldRef.current === "body" ? undefined : "end").insertContent(emoji).run();
      setHtmlBody(ed.getHTML());
    } else {
      setHtmlBody((prev) => prev + emoji);
    }
  }, []);

  const handleFilesSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? []);
      // Reset the input so re-picking the same file fires onChange again.
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (picked.length === 0) return;

      const accepted: File[] = [];
      for (const file of picked) {
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
          toast.error(`File type not allowed: ${file.name}`);
          continue;
        }
        if (file.size > MAX_FILE_BYTES) {
          toast.error(`File exceeds 15MB limit: ${file.name}`);
          continue;
        }
        accepted.push(file);
      }
      if (accepted.length > 0) {
        setFiles((prev) => [...prev, ...accepted]);
      }
    },
    [],
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const removeDoc = useCallback((fileName: string) => {
    setSelectedDocs((prev) => prev.filter((d) => d !== fileName));
  }, []);

  const handleOpenChange = useCallback(
    (v: boolean) => {
      if (!v) reset();
      onOpenChange(v);
    },
    [onOpenChange, reset],
  );

  const handleSend = useCallback(async () => {
    if (!subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    if (!htmlBody.trim() || htmlBody === "<p></p>") {
      toast.error("Email body is required");
      return;
    }
    setSending(true);
    try {
      let finalBody = htmlBody;
      if (replyTo?.htmlBody) {
        finalBody += `<br/><blockquote style="border-left:3px solid #ccc;padding-left:12px;margin-left:0;color:#666">${replyTo.htmlBody}</blockquote>`;
      }
      await onSend(subject, finalBody, replyTo?.replyID, recipients, files, selectedDocs);
      toast.success("Email sent");
      handleOpenChange(false);
    } catch (err) {
      // Surface the actual reason. Attachment rejections (ATTACHMENT_ERROR),
      // opt-out blocks, timeouts and failed S3 uploads all reach here with a
      // specific message; a bare `catch` used to discard every one of them.
      toast.error(getErrorMessage(err, "Failed to send email"));
    } finally {
      setSending(false);
    }
  }, [subject, htmlBody, recipients, files, selectedDocs, onSend, handleOpenChange, replyTo]);

  const handleApplyHeaderFooter = useCallback(
    (header: string, footer: string) => {
      const ed = editorRef.current;
      if (ed) {
        const current = ed.getHTML();
        const wrapped = header + current + footer;
        ed.chain().selectAll().insertContent(wrapped).run();
        setHtmlBody(ed.getHTML());
      } else {
        setHtmlBody((prev) => header + prev + footer);
      }
    },
    [],
  );

  const handleApplyContent = useCallback(
    (content: string, tplSubject?: string) => {
      const ed = editorRef.current;
      if (ed) {
        ed.commands.insertContent(content);
        setHtmlBody(ed.getHTML());
      } else {
        setHtmlBody((prev) => prev + content);
      }
      if (tplSubject) {
        setSubject((prev) => prev || tplSubject);
      }
    },
    [],
  );

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-6xl w-[90vw] sm:max-w-none max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {replyTo && <Reply className="h-5 w-5 text-primary" />}
              {replyTo ? "Reply to Email" : "Compose Email"}
            </DialogTitle>
            <DialogDescription>
              {replyTo ? "Replying to" : "Send an email to"} {patientName}
              {patientEmail && (
                <span className="text-foreground font-medium"> ({patientEmail})</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto">
            {patientEmail && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-muted-foreground w-12">To</label>
                <div className="flex-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                  {patientEmail}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <label htmlFor="email-subject" className="text-sm font-medium text-muted-foreground w-12">
                Subject
              </label>
              <input
                id="email-subject"
                ref={subjectRef}
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                onFocus={() => { lastFieldRef.current = "subject"; }}
                placeholder="Email subject..."
                className="flex-1 rounded-md border border-border bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
              />
            </div>

            <div className="flex items-start gap-2">
              <label className="text-sm font-medium text-muted-foreground w-12 pt-1.5">CC</label>
              <div className="flex-1">
                <RecipientPicker
                  value={recipients}
                  onChange={setRecipients}
                  planId={planId}
                  disabled={sending}
                />
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTemplatePickerOpen(true)}
                className="gap-1.5 text-amber-600 border-amber-200 hover:bg-amber-50 hover:text-amber-700"
              >
                <Zap className="h-3.5 w-3.5" />
                Templates
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending}
                className="gap-1.5"
              >
                <Paperclip className="h-3.5 w-3.5" />
                Attach
              </Button>
              {patientId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDocPickerOpen(true)}
                  disabled={sending}
                  className="gap-1.5"
                >
                  <FileText className="h-3.5 w-3.5" />
                  Documents
                </Button>
              )}
              <EmojiPickerButton
                onSelect={insertEmoji}
                disabled={sending}
                title="Insert emoji where the cursor is, in the subject or the message"
                side="bottom"
                portal={false}
                className={buttonVariants({ variant: "outline", size: "sm", className: "gap-1.5" })}
              >
                <Smile className="h-3.5 w-3.5" />
                Emoji
              </EmojiPickerButton>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT_ATTR}
                onChange={handleFilesSelected}
                className="hidden"
              />
            </div>

            {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {files.map((file, index) => (
                  <span
                    key={`${file.name}-${index}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs text-foreground"
                  >
                    <Paperclip className="h-3 w-3 text-muted-foreground" />
                    <span className="max-w-[200px] truncate">{file.name}</span>
                    <span className="text-muted-foreground">
                      ({formatBytes(file.size)})
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      disabled={sending}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {selectedDocs.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedDocs.map((doc) => (
                  <span
                    key={doc}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs text-foreground"
                  >
                    <FileText className="h-3 w-3 text-muted-foreground" />
                    <span className="max-w-[200px] truncate">{doc}</span>
                    <button
                      type="button"
                      onClick={() => removeDoc(doc)}
                      disabled={sending}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label={`Remove ${doc}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* `contents` so the wrapper only observes focus and adds no box. */}
            <div className="contents" onFocus={() => { lastFieldRef.current = "body"; }}>
              <TiptapEditor
                content={htmlBody}
                onChange={setHtmlBody}
                placeholder={replyTo ? "Type your reply..." : "Compose your email..."}
                className="flex-1 min-h-[300px]"
                editorClassName="min-h-[260px]"
                editorRef={editorRef}
              />
            </div>

            {replyTo?.htmlBody && (
              <div className="border border-border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowOriginal((v) => !v)}
                  className="flex items-center gap-1.5 w-full px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                >
                  {showOriginal ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                  Original message
                </button>
                {showOriginal && (
                  <iframe
                    sandbox="allow-same-origin"
                    srcDoc={replyTo.htmlBody}
                    className="w-full border-t border-border block"
                    style={{ height: "240px" }}
                    title="Original email"
                  />
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={sending}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={sending} className="gap-2">
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Send Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <EmailTemplatePicker
        open={templatePickerOpen}
        onOpenChange={setTemplatePickerOpen}
        onApplyHeaderFooter={handleApplyHeaderFooter}
        onApplyContent={handleApplyContent}
        patientId={patientId}
        planId={planId}
      />

      <DocumentSelectModal
        open={docPickerOpen}
        onOpenChange={setDocPickerOpen}
        patientId={patientId}
        selected={selectedDocs}
        onConfirm={setSelectedDocs}
      />
    </>
  );
}
